import { FC, Fragment } from 'react';

import dayjs from 'dayjs';
import { CalendarDays, CircleCheckBig } from 'lucide-react';
import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Pagination from '@mui/material/Pagination';

import css from './ReferralsDialog.module.css';

import { referralsService } from 'v2/api/referralsService';
import { Card } from 'v2/components/card/Card';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { UserAvatar } from 'v2/components/userAvatar/UserAvatar';
import { usePagination } from 'v2/hooks/usePagination';
import { UserModel } from 'v2/types/UserModel';
import { getPaginationCount } from 'v2/utils';

interface ReferralsDialogProps {
  user: UserModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const ReferralsDialog: FC<ReferralsDialogProps> = ({ user, isOpen, onClose }) => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 4 });

  const { data } = useSWR(isOpen && ['getUserReferrals', page, pageSize, user.id], referralsService.getUserReferrals, {
    keepPreviousData: true,
  });

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>User Referrals</DialogTitle>

      <DialogContent className={css.referralsDialogContent}>
        {isNonNullish(data) && (
          <Fragment>
            {isEmpty(data.result) ? (
              <EmptyPlaceholder />
            ) : (
              <Fragment>
                {data.result.map((referral) => (
                  <Card key={referral.id} className={css.referralCard}>
                    <div className={css.referralCardHeader}>
                      <UserAvatar imageUrl={referral.imgUrl} />

                      <div className={css.referralData}>
                        <p className={css.referralNickname}>{referral.userNickname}</p>

                        <p className={css.referralJoinDate}>
                          <CalendarDays width={16} height={16} />
                          Joined {dayjs(referral.registrationDate).format('MMM DD, YYYY')}
                        </p>
                      </div>
                    </div>

                    <div className={css.referralCardTasks}>
                      <div className={css.referralCardTasksTitle}>
                        <CircleCheckBig width={17} height={17} />
                        Tasks Completed
                      </div>

                      <Chip label={referral.completedTasksCount} size="small" />
                    </div>
                  </Card>
                ))}

                <Pagination
                  page={page}
                  count={getPaginationCount(pageSize, data.total)}
                  onChange={(_, page) => onPageChange(page)}
                />
              </Fragment>
            )}
          </Fragment>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
